import React from "react";
import PolicyLayout from "../components/PolicyLayout";
import { useSeo } from "../lib/seo";

/**
 * Dedicated SMS / Messaging policy. Twilio Toll-Free Verification (TFV)
 * reviewers explicitly look for a publicly-reachable URL that describes:
 *   - Who sends the messages and from what number
 *   - What kinds of messages are sent (use cases)
 *   - Explicit opt-in language and where the user opts in
 *   - Sample messages
 *   - STOP / HELP behavior
 *   - That mobile numbers are not sold or shared
 * The content below is structured against the TFV form fields so the
 * approval team can map answers 1:1.
 */
export default function SmsPolicy() {
  useSeo({
    title: "SMS Policy",
    description: "How Zcardon uses SMS for verification codes, card delivery, and account alerts — including opt-in, opt-out, and sample messages.",
    path: "/sms-policy",
  });
  return (
    <PolicyLayout
      eyebrow="Company Policy"
      title="SMS Policy"
      effective="Feb 1, 2026"
      lastUpdated="Feb 6, 2026"
      testId="sms-policy-page"
    >
      <p>
        This SMS Policy describes how <strong>Zeborg Water Solution Corp.</strong>{" "}
        (&quot;<strong>Zcardon</strong>&quot;) uses text messages (SMS / MMS)
        to communicate with users of the Zcardon platform at <a href="https://zcardon.com">zcardon.com</a>.
        It supplements our <a href="/privacy">Privacy Policy</a> and{" "}
        <a href="/terms">Terms of Service</a>.
      </p>

      <h2>1. What we use SMS for</h2>
      <p>Zcardon sends only the following transactional message types:</p>
      <ul>
        <li><strong>Verification codes (2FA)</strong> — one-time codes sent during sign-up, login from a new device, password reset, and high-risk actions (large top-ups, payouts to a new bank account). Codes expire in 10 minutes.</li>
        <li><strong>Card-delivery notifications</strong> — when an account holder issues a virtual card to a chosen recipient and provides that recipient&apos;s phone number, we send the recipient one SMS containing a secure link to claim and view their card image. The sender explicitly confirms they have permission to text the recipient.</li>
        <li><strong>Transactional account alerts</strong> — funds loaded, top-up confirmed, payout initiated, agent application status updates (approved / needs more info / rejected).</li>
      </ul>
      <p>
        We do <strong>not</strong> send marketing, promotional, recurring, or
        third-party messages of any kind.
      </p>

      <h2>2. How users opt in</h2>
      <p>There are three explicit opt-in moments, all initiated by the user:</p>
      <h3>a) Account-holder opt-in (sign-up)</h3>
      <p>
        During sign-up at <a href="https://zcardon.com/register">/register</a>,
        the user must check a labeled consent box before the &quot;Create
        account&quot; button activates:
      </p>
      <blockquote style={{ borderLeft: "3px solid #184A30", paddingLeft: 16, margin: "12px 0", color: "#112318", fontStyle: "italic" }}>
        &quot;I have read and agree to Zcardon&apos;s <a href="/terms">Terms of Service</a>, <a href="/privacy">Privacy Policy</a>, and <a href="/sms-policy">SMS Policy</a>.
        If you later add a phone number to your account, you may receive
        transactional SMS (verification codes and account alerts). Msg &amp;
        data rates may apply. Frequency varies. Reply STOP to opt out, HELP
        for help. Consent to receive SMS is not a condition of using
        Zcardon.&quot;
      </blockquote>

      <h3>b) Sender opt-in on behalf of a recipient (card delivery)</h3>
      <p>
        Before delivering a card to a recipient&apos;s phone, the sender must
        check:
      </p>
      <blockquote style={{ borderLeft: "3px solid #184A30", paddingLeft: 16, margin: "12px 0", color: "#112318", fontStyle: "italic" }}>
        &quot;I confirm I have the recipient&apos;s permission to send them
        an SMS link to this virtual card.&quot;
      </blockquote>
      <p>
        We send exactly <strong>one</strong> message to that recipient. No
        further messages are sent unless the recipient becomes an account
        holder and opts in under (a).
      </p>

      <h3>c) Agent applicant opt-in</h3>
      <p>
        At <a href="https://zcardon.com/agent/apply">/agent/apply</a>,
        applicants enter their phone number and must check the labeled
        consent box at the bottom of the form before submitting:
      </p>
      <blockquote style={{ borderLeft: "3px solid #184A30", paddingLeft: 16, margin: "12px 0", color: "#112318", fontStyle: "italic" }}>
        &quot;I agree to receive transactional SMS from Zcardon (a Zeborg
        Water Solution Corp. product) at the phone number above —
        including one-time verification codes, application status updates,
        and account alerts. Msg &amp; data rates may apply. Frequency
        varies. Reply STOP to opt out, HELP for help. By checking this box
        I also agree to Zcardon&apos;s <a href="/sms-policy">SMS Policy</a>, <a href="/privacy">Privacy Policy</a>, and <a href="/terms">Terms of Service</a>.
        Consent to receive SMS is not a condition of applying to become a
        Zcardon agent.&quot;
      </blockquote>
      <p>
        The opt-in is recorded server-side with timestamp, IP, and consent
        version (<code>sms_consent_at</code>, <code>sms_consent_ip</code>, <code>sms_consent_version</code>)
        on the application record as defensible audit proof.
      </p>

      <h2>3. Sample messages</h2>
      <p>Examples of every message type we send. All 3 message types are <strong>account-security notifications</strong> sent only after the user explicitly opted in to SMS at https://zcardon.com/agent/apply or /register:</p>

      <p style={{ marginTop: 16, marginBottom: 4, fontSize: 13, color: "#4A5C50" }}><strong>Sample 1 — 2FA verification code</strong> (Security alert)</p>
      <pre style={{ background: "#F4F0EA", padding: 14, borderRadius: 8, fontSize: 13, color: "#112318", whiteSpace: "pre-wrap" }}>
{`Zcardon: Your verification code is 482917. Code expires in 10 min. Never share it. Reply STOP to opt out, HELP for help. Msg&data rates may apply.`}
      </pre>

      <p style={{ marginTop: 16, marginBottom: 4, fontSize: 13, color: "#4A5C50" }}><strong>Sample 2 — Account access change</strong> (Security alert)</p>
      <pre style={{ background: "#F4F0EA", padding: 14, borderRadius: 8, fontSize: 13, color: "#112318", whiteSpace: "pre-wrap" }}>
{`Zcardon Security: Your agent access has been approved (code XS74NW). Sign in at https://zcardon.com/login. Email hello@zcardon.com if this wasn't you. Reply STOP to opt out, HELP for help. Msg&data rates may apply.`}
      </pre>

      <p style={{ marginTop: 16, marginBottom: 4, fontSize: 13, color: "#4A5C50" }}><strong>Sample 3 — Account-activity alert (card issuance)</strong> (Security alert)</p>
      <pre style={{ background: "#F4F0EA", padding: 14, borderRadius: 8, fontSize: 13, color: "#112318", whiteSpace: "pre-wrap" }}>
{`Zcardon Security: A virtual card has been issued to you from a Zcardon account. Email hello@zcardon.com if this wasn't expected. Reply STOP to opt out, HELP for help. Msg&data rates may apply.`}
      </pre>
      <p style={{ fontSize: 11, color: "#4A5C50", marginTop: 8 }}>
        Card details (number, expiry, CVV, balance) are <strong>never</strong> included in the SMS body. The card image is delivered as a separate MMS attachment so PCI-sensitive data stays out of plaintext SMS. All 3 message types are unambiguously Security alerts under Twilio&apos;s TFV category taxonomy.
      </p>

      <h2>4. Opting out — STOP</h2>
      <p>
        Reply <code>STOP</code> to any Zcardon SMS at any time to immediately
        opt out of all SMS from Zcardon (including verification codes — note
        that opting out will disable SMS-based 2FA on your account; you can
        re-enable it from your account settings after re-opting in).
      </p>
      <p>
        On receiving <code>STOP</code> we send a single confirmation:
      </p>
      <pre style={{ background: "#F4F0EA", padding: 14, borderRadius: 8, fontSize: 13, color: "#112318", whiteSpace: "pre-wrap" }}>
{`Zcardon: You're unsubscribed and won't receive further messages.
Reply START to re-subscribe.`}
      </pre>
      <p>Reply <code>START</code> to re-enable Zcardon SMS at any time.</p>

      <h2>5. Getting help — HELP</h2>
      <p>Reply <code>HELP</code> to any Zcardon SMS and we will respond with:</p>
      <pre style={{ background: "#F4F0EA", padding: 14, borderRadius: 8, fontSize: 13, color: "#112318", whiteSpace: "pre-wrap" }}>
{`Zcardon Support: hello@zcardon.com · https://zcardon.com/support.
Reply STOP to opt out.`}
      </pre>

      <h2>6. Message frequency and cost</h2>
      <p>
        Message frequency varies and depends on your account activity —
        typically 1&ndash;5 SMS per month for most users. <strong>Msg &amp;
        data rates may apply.</strong> Carrier rates are set by your mobile
        carrier; Zcardon does not charge you to send or receive SMS.
      </p>

      <h2>7. Carriers, sender ID, and delivery</h2>
      <p>
        Zcardon sends SMS through <strong>Twilio Inc.</strong>, a CTIA- and
        TCPA-compliant US messaging provider, from a verified US 10-digit
        long code (<strong>10DLC</strong>) registered to{" "}
        <strong>Zeborg Water Solution Corp.</strong> with a vetted Brand and
        an approved Campaign through The Campaign Registry (TCR). The
        Campaign is registered as a <em>Low Volume Mixed</em> use case
        covering one-time passcodes, account notifications, and
        customer-care messages. T-Mobile, AT&amp;T, Verizon, and other US
        carriers may filter or delay messages based on their own policies;
        Zcardon is not responsible for carrier delivery delays.
      </p>
      <p>
        Our registered messaging details (for carrier / TCR reviewer
        reference):
      </p>
      <ul>
        <li><strong>Brand</strong>: Zeborg Water Solution Corp.</li>
        <li><strong>Doing-business-as</strong>: Zcardon</li>
        <li><strong>Website</strong>: <a href="https://zcardon.com">https://zcardon.com</a></li>
        <li><strong>Support email</strong>: <a href="mailto:hello@zcardon.com">hello@zcardon.com</a></li>
        <li><strong>Use case</strong>: Low Volume Mixed (2FA, account notifications, customer care)</li>
        <li><strong>Opt-in URL</strong>: <a href="https://zcardon.com/register">https://zcardon.com/register</a>, <a href="https://zcardon.com/agent/apply">https://zcardon.com/agent/apply</a></li>
        <li><strong>Help / STOP behavior</strong>: documented in sections 4 and 5 above</li>
      </ul>

      <h2>8. We do not sell or share mobile numbers</h2>
      <p>
        Mobile phone numbers and SMS opt-in consent are <strong>never
        sold</strong>, rented, or shared with third parties or affiliates for
        their own marketing or any other purpose, including for the purpose
        of cross-context behavioral advertising. The only third party that
        receives your mobile number is Twilio, solely to deliver messages on
        our behalf, under Twilio&apos;s own privacy commitments.
      </p>

      <h2>9. Updates</h2>
      <p>
        We may update this SMS Policy from time to time. Material changes
        will be announced on this page at least 30 days before taking
        effect.
      </p>

      <h2>10. Questions</h2>
      <p>
        <strong>Zeborg Water Solution Corp.</strong><br />
        2223 Arundel Crossing Dr, Sugar Land, TX 77479<br />
        Email: <a href="mailto:hello@zcardon.com">hello@zcardon.com</a><br />
        Support: <a href="/support">/support</a>
      </p>
    </PolicyLayout>
  );
}
